import {
    TActivePlanCode,
    TCouponApplicablePricingPeriod,
    TPricingPeriod
} from '~/components/Membership/types/membership.types';
import { TFuncKey } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';

import { Coupons } from '~/constants/coupons';

/**
 * "Flexible coupons" are coupons that do not apply to all plans
 * or do not offer the same discount for all plans
 */

export type TCodePerPlan = Partial<Record<TActivePlanCode, string>>;

type TFlexibleCouponBannerInfo = {
    fixedPrice?: number;
    planCode?: TActivePlanCode;
};

type TPromoBannerInfo = TFlexibleCouponBannerInfo & {
    customTextKey?: TFuncKey<
        typeof NAMESPACES.MEMBERSHIP,
        'promo_banner.custom_text'
    >;
    customTextKeyMobile?: TFuncKey<
        typeof NAMESPACES.MEMBERSHIP,
        'promo_banner.custom_text'
    >;
    shouldShowBadge?: boolean;
};

type TDiscountBannerInfo = TFlexibleCouponBannerInfo & {
    customTextKey?: TFuncKey<
        typeof NAMESPACES.MEMBERSHIP,
        'pricing_plans.discount_banner.custom_text'
    >;
};

export type TFlexibleCoupon = {
    codePerPlan: TCodePerPlan;
    // The pricing period to which the coupon applies (eg: "annual" if coupon is applicable to annual plans only)
    applicablePricingPeriod?: TCouponApplicablePricingPeriod;
    // The default pricing period to display the prices for by default
    defaultPricingPeriod?: TPricingPeriod;
    promoBannerInfo: TPromoBannerInfo;
    discountBannerInfo: Partial<Record<TPricingPeriod, TDiscountBannerInfo>>;
    periodSwitchTag?: {
        monthNb?: number;
        percentage?: number;
    };
    hidePromoBannerBadgeOnMobile?: boolean;
};

export const FLEXIBLE_COUPONS: Record<string, TFlexibleCoupon> = {
    [Coupons.wmba24]: {
        codePerPlan: {
            essentials_annual: 'wmba24_essentials_annual',
            premium_annual: 'wmba24_premium_annual'
        },
        applicablePricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'essentials_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'essentials_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 2
        }
    },
    [Coupons.prismm]: {
        codePerPlan: {
            starter_monthly_2023: 'prismm_starter_monthly',
            essentials_monthly: 'prismm_essentials_monthly',
            premium_monthly: 'prismm_premium_monthly',
            starter_annual: 'prismm_starter_annual',
            essentials_annual: 'prismm_essentials_annual',
            premium_annual: 'prismm_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                customTextKey: 'prismm'
            },
            annual: {
                customTextKey: 'prismm'
            }
        },
        periodSwitchTag: {
            monthNb: 2
        }
    },
    [Coupons.levelup30]: {
        codePerPlan: {
            starter_monthly_2023: 'levelup30_starter_monthly',
            essentials_monthly: 'levelup30_essentials_monthly',
            premium_monthly: 'levelup30_premium_monthly',
            starter_annual: 'levelup30_starter_annual',
            essentials_annual: 'levelup30_essentials_annual',
            premium_annual: 'levelup30_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 2
        }
    },
    [Coupons.priceupdate20]: {
        codePerPlan: {
            starter_monthly_2023: '2025_starter_monthly',
            essentials_monthly: '2025_essentials_monthly',
            premium_monthly: '2025_premium_monthly',
            starter_annual: '2025_starter_annual',
            essentials_annual: '2025_essentials_annual',
            premium_annual: '2025_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            fixedPrice: 1,
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                fixedPrice: 1,
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        }
    },
    [Coupons.beccaluna]: {
        codePerPlan: {
            essentials_annual: 'beccaluna25_essentials_annual',
            premium_annual: 'beccaluna25_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'essentials_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'essentials_annual'
            }
        }
    },
    [Coupons.adobe50]: {
        codePerPlan: {
            starter_monthly_2023: 'adobe_starter_monthly',
            essentials_monthly: 'adobe_essentials_monthly',
            premium_monthly: 'adobe_premium_monthly',
            starter_annual: 'adobe_starter_annual',
            essentials_annual: 'adobe_essentials_annual',
            premium_annual: 'adobe_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 2
        }
    },
    [Coupons.grow30]: {
        codePerPlan: {
            starter_annual: 'grow30_starter_annual',
            essentials_annual: 'grow30_essentials_annual',
            premium_annual: 'grow30_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'starter_annual',
            customTextKey: 'grow30',
            customTextKeyMobile: 'grow30_mobile'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'starter_annual'
            }
        }
    },
    [Coupons.freelancersunion]: {
        codePerPlan: {
            starter_monthly_2023: 'fu_starter_monthly',
            essentials_monthly: 'fu_essentials_monthly',
            premium_monthly: 'fu_premium_monthly',
            starter_annual: 'fu_starter_annual',
            essentials_annual: 'fu_essentials_annual',
            premium_annual: 'fu_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 2
        }
    },
    [Coupons.hb2024save]: {
        codePerPlan: {
            starter_monthly_2023: 'hb2024save_starter_monthly',
            essentials_monthly: 'hb2024save_essentials_monthly',
            premium_monthly: 'hb2024save_premium_monthly',
            starter_annual: 'hb2024save_starter_annual',
            essentials_annual: 'hb2024save_essentials_annual',
            premium_annual: 'hb2024save_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            fixedPrice: 1,
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                fixedPrice: 1,
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 6
        }
    },
    [Coupons.cyber23]: {
        codePerPlan: {
            starter_monthly_2023: 'cyber23_starter_monthly',
            essentials_monthly: 'cyber23_essentials_monthly',
            premium_monthly: 'cyber23_premium_monthly',
            starter_annual: 'cyber23_starter_annual',
            essentials_annual: 'cyber23_essentials_annual',
            premium_annual: 'cyber23_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            fixedPrice: 1,
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                fixedPrice: 1,
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 2
        }
    },
    [Coupons.welcome50]: {
        codePerPlan: {
            starter_monthly_2023: 'evergreen_starter_monthly',
            essentials_monthly: 'evergreen_essentials_monthly',
            premium_monthly: 'evergreen_premium_monthly',
            starter_annual: 'evergreen_starter_annual',
            essentials_annual: 'evergreen_essentials_annual',
            premium_annual: 'evergreen_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'starter_annual'
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 2
        },
        hidePromoBannerBadgeOnMobile: true
    },
    [Coupons.getbalance]: {
        codePerPlan: {
            starter_monthly_2023: 'get_balance_starter_monthly',
            essentials_monthly: 'get_balance_essentials_monthly',
            premium_monthly: 'get_balance_premium_monthly',
            starter_annual: 'get_balance_starter_annual',
            essentials_annual: 'get_balance_essentials_annual',
            premium_annual: 'get_balance_premium_annual'
        },
        promoBannerInfo: {
            planCode: 'starter_annual'
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        }
    },
    [Coupons.test_flexible_coupon]: {
        codePerPlan: {
            starter_monthly_2023: 'test_flexible_starter_monthly',
            essentials_monthly: 'test_flexible_essentials_monthly',
            premium_monthly: 'test_flexible_premium_monthly',
            starter_annual: 'test_flexible_starter_annual',
            essentials_annual: 'test_flexible_essentials_annual',
            premium_annual: 'test_flexible_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            fixedPrice: 1,
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                fixedPrice: 1,
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 6
        }
    },
    test_flexible_1: {
        codePerPlan: {
            starter_monthly_2023: 'test_flexible_1_starter_monthly',
            essentials_monthly: 'test_flexible_1_essentials_monthly',
            premium_monthly: 'test_flexible_1_premium_monthly',
            starter_annual: 'test_flexible_1_starter_annual',
            essentials_annual: 'test_flexible_1_essentials_annual',
            premium_annual: 'test_flexible_1_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            fixedPrice: 1,
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                fixedPrice: 1,
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 6
        }
    },
    [Coupons.test_flexible_monthly_only]: {
        codePerPlan: {
            starter_monthly_2023: 'test_flexible_starter_monthly',
            essentials_monthly: 'test_flexible_essentials_monthly',
            premium_monthly: 'test_flexible_premium_monthly'
        },
        applicablePricingPeriod: 'monthly',
        promoBannerInfo: {
            fixedPrice: 1,
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                fixedPrice: 1,
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        }
    },
    [Coupons.joinhb]: {
        codePerPlan: {
            starter_monthly_2023: 'joinhb_starter_monthly',
            essentials_monthly: 'joinhb_essentials_monthly',
            premium_monthly: 'joinhb_premium_monthly',
            starter_annual: 'joinhb_annual',
            essentials_annual: 'joinhb_annual',
            premium_annual: 'joinhb_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            fixedPrice: 1,
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                fixedPrice: 1,
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        periodSwitchTag: {
            monthNb: 6
        }
    },
    [Coupons.take90]: {
        codePerPlan: {
            starter_monthly_2023: 'take90',
            essentials_monthly: 'take90',
            premium_monthly: 'take90'
        },
        applicablePricingPeriod: 'monthly',
        promoBannerInfo: {
            planCode: 'starter_monthly_2023'
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            }
        }
    },
    [Coupons.annual30]: {
        codePerPlan: {
            starter_annual: 'annual30',
            essentials_annual: 'annual30',
            premium_annual: 'annual30'
        },
        applicablePricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'starter_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'starter_annual'
            }
        }
    },
    [Coupons.pictime]: {
        codePerPlan: {
            starter_monthly_2023: 'pictime',
            essentials_monthly: 'pictime',
            starter_annual: 'pictime',
            essentials_annual: 'pictime'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            fixedPrice: 0,
            planCode: 'starter_annual'
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        }
    },
    [Coupons.urgent]: {
        codePerPlan: {
            starter_monthly_2023: 'start50_urgency_starter_monthly',
            essentials_monthly: 'start50_urgency_essentials_monthly',
            premium_monthly: 'start50_urgency_premium_monthly',
            starter_annual: 'start50_urgency_starter_annual',
            essentials_annual: 'start50_urgency_essentials_annual',
            premium_annual: 'start50_urgency_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'starter_monthly_2023',
            shouldShowBadge: false
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        hidePromoBannerBadgeOnMobile: true
    },
    [Coupons.save50]: {
        codePerPlan: {
            starter_monthly_2023: 'save50_urgency_starter_monthly',
            essentials_monthly: 'save50_urgency_essentials_monthly',
            premium_monthly: 'save50_urgency_premium_monthly',
            starter_annual: 'save50_urgency_starter_annual',
            essentials_annual: 'save50_urgency_essentials_annual',
            premium_annual: 'save50_urgency_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'starter_monthly_2023',
            customTextKey: 'urgent_control',
            shouldShowBadge: false
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        hidePromoBannerBadgeOnMobile: true
    },
    [Coupons.join50]: {
        codePerPlan: {
            starter_monthly_2023: 'join50_starter_monthly',
            essentials_monthly: 'join50_essentials_monthly',
            premium_monthly: 'join50_premium_monthly',
            starter_annual: 'join50_starter_annual',
            essentials_annual: 'join50_essentials_annual',
            premium_annual: 'join50_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'starter_monthly_2023',
            customTextKey: 'join50',
            shouldShowBadge: false
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        hidePromoBannerBadgeOnMobile: true
    },
    [Coupons.join30]: {
        codePerPlan: {
            starter_monthly_2023: 'join30_starter_monthly',
            essentials_monthly: 'join30_essentials_monthly',
            premium_monthly: 'join30_premium_monthly',
            starter_annual: 'join30_starter_annual',
            essentials_annual: 'join30_essentials_annual',
            premium_annual: 'join30_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'starter_monthly_2023',
            customTextKey: 'join30',
            shouldShowBadge: false
        },
        discountBannerInfo: {
            monthly: {
                planCode: 'starter_monthly_2023'
            },
            annual: {
                planCode: 'starter_annual'
            }
        },
        hidePromoBannerBadgeOnMobile: true
    },
    [Coupons.cyber24]: {
        codePerPlan: {
            starter_annual: 'cyber24_starter_annual',
            essentials_annual: 'cyber24_essentials_annual',
            premium_annual: 'cyber24_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'starter_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'starter_annual'
            }
        }
    },
    [Coupons.off25]: {
        codePerPlan: {
            essentials_annual: 'jan25_essential_annual',
            premium_annual: 'jan25_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'essentials_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'essentials_annual'
            }
        }
    },
    // This is for backwards compatibility - remove after https://honeybook.atlassian.net/browse/GT-1476
    [Coupons.jan25]: {
        codePerPlan: {
            essentials_annual: 'jan25_essential_annual',
            premium_annual: 'jan25_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'essentials_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'essentials_annual'
            }
        }
    },
    [Coupons.wppi30]: {
        codePerPlan: {
            starter_annual: 'wppi30_starter_annual',
            essentials_annual: 'wppi30_essential_annual',
            premium_annual: 'wppi30_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'starter_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'starter_annual'
            }
        }
    },
    [Coupons.imagen30]: {
        codePerPlan: {
            starter_annual: 'imagen30_starter_annual',
            essentials_annual: 'imagen30_essential_annual',
            premium_annual: 'imagen30_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'starter_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'starter_annual'
            }
        }
    },
    [Coupons.blazeai30]: {
        codePerPlan: {
            essentials_annual: 'blazeai30_essential_annual',
            premium_annual: 'blazeai30_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'essentials_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'essentials_annual'
            }
        }
    },
    [Coupons.rella30]: {
        codePerPlan: {
            essentials_annual: 'rella30_essential_annual',
            premium_annual: 'rella30_premium_annual'
        },
        defaultPricingPeriod: 'annual',
        promoBannerInfo: {
            planCode: 'essentials_annual'
        },
        discountBannerInfo: {
            annual: {
                planCode: 'essentials_annual'
            }
        }
    },
    [Coupons.timelinegenius]: {
        codePerPlan: {
            essentials_annual: 'timeline_genius_essential',
            premium_annual: 'timeline_genius_premium'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'annual',
        promoBannerInfo: {
            fixedPrice: 0,
            planCode: 'essentials_annual'
        },
        discountBannerInfo: {
            annual: {
                customTextKey: 'timeline_genius_flow3'
            }
        },
        periodSwitchTag: {
            percentage: 50,
            monthNb: 0
        }
    },
    [Coupons.timelinegenius1yf]: {
        codePerPlan: {
            essentials_annual: 'timeline_genius_essential_1yf',
            premium_annual: 'timeline_genius_premium_1yf'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'annual',
        promoBannerInfo: {
            fixedPrice: 0,
            planCode: 'essentials_annual'
        },
        discountBannerInfo: {
            annual: {
                customTextKey: 'timeline_genius_flow3'
            }
        },
        periodSwitchTag: {
            percentage: 50,
            monthNb: 0
        }
    },
    [Coupons.weven]: {
        codePerPlan: {
            essentials_annual: 'weven_essential_1yf',
            premium_annual: 'weven_premium_1yf'
        },
        defaultPricingPeriod: 'annual',
        applicablePricingPeriod: 'default',
        promoBannerInfo: {
            planCode: 'essentials_annual'
        },
        discountBannerInfo: {
            annual: {
                customTextKey: 'weven_flow'
            },
            monthly: {
                customTextKey: 'weven_flow'
            }
        }
    }
} as const;

export const PRICE_CHANGE_DATE = '2025-02-04';
